import React, { useState, useEffect } from 'react';
import { Upload, Download, Trash2, Search, File, Folder, Eye, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';
import PDFViewer from '../ui/PDFViewer';

const Files = () => {
  const [files, setFiles] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [filesRes, clientsRes] = await Promise.all([
        api.get('/files'),
        api.get('/clients')
      ]);
      setFiles(filesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (selectedClient && selectedClient !== 'all') {
      formData.append('client_id', selectedClient);
    }

    try {
      await api.post('/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('File uploaded successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await api.get(`/files/${fileId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await api.delete(`/files/${fileId}`);
      toast.success('File deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to delete file:', error);
      toast.error('Failed to delete file');
    }
  };

  const handlePreview = async (file) => {
    // Expanded file preview support
    const isImage = file.mime_type?.startsWith('image/');
    const isPdf = file.mime_type === 'application/pdf';
    const isText = file.mime_type === 'text/plain' || 
                   file.mime_type === 'text/csv' ||
                   file.mime_type === 'application/json';
    const isOfficeDoc = file.mime_type === 'application/msword' ||
                        file.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                        file.mime_type === 'application/vnd.ms-excel' ||
                        file.mime_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                        file.mime_type === 'application/vnd.ms-powerpoint' ||
                        file.mime_type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    
    const canPreview = isImage || isPdf || isText || isOfficeDoc;
    
    if (!canPreview) {
      toast.info('This file type cannot be previewed. Downloading instead...');
      handleDownload(file.id, file.filename);
      return;
    }

    try {
      // For Office documents, show a message and offer download
      // Google Docs Viewer requires publicly accessible URLs, which we don't have for secure files
      if (isOfficeDoc) {
        setPreviewFile({ ...file, isOfficeDoc: true });
        setPreviewUrl(null);
      }
      // For PDFs, use direct URL with token for rendering
      else if (isPdf) {
        const token = localStorage.getItem('token');
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const directUrl = `${backendUrl}/api/files/${file.id}/preview?token=${token}`;
        setPreviewUrl(directUrl);
        setPreviewFile(file);
      } else {
        // For images and text, use blob approach
        const response = await api.get(`/files/${file.id}/preview`, {
          responseType: 'blob'
        });
        const blob = new Blob([response.data], { type: file.mime_type });
        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setPreviewFile(file);
      }
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error('Failed to load preview');
    }
  };

  const closePreview = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewFile(null);
    setPreviewUrl(null);
  };

  const filteredFiles = files.filter(file => {
    const matchesSearch = file.filename.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = selectedClient === 'all' || file.client_id === selectedClient;
    return matchesSearch && matchesClient;
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="files-title">File Management</h2>
          <p className="text-gray-600">Upload and manage client files</p>
        </div>
        <div>
          <input
            type="file"
            id="file-upload"
            data-testid="file-upload-input"
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <label htmlFor="file-upload">
            <Button
              data-testid="upload-button"
              as="span"
              disabled={uploading}
              className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white cursor-pointer"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>
          </label>
        </div>
      </div>

      {/* Filters */}
      <div className="glass rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              data-testid="search-files-input"
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger data-testid="client-filter-select">
              <SelectValue placeholder="Filter by client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.company_name || `Client ${client.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Files Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredFiles.length === 0 ? (
          <div className="col-span-full glass rounded-xl p-12 text-center">
            <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No files found' : 'No files yet. Upload your first file!'}
            </p>
          </div>
        ) : (
          filteredFiles.map((file, index) => (
            <div
              key={file.id}
              className="glass rounded-xl p-6 hover-lift transition-all"
              data-testid={`file-card-${index}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-teal-100 to-cyan-100 rounded-lg flex items-center justify-center">
                  <File className="w-6 h-6 text-teal-600" />
                </div>
                <div className="flex items-center space-x-2">
                  {(file.mime_type?.startsWith('image/') || 
                    file.mime_type === 'application/pdf' ||
                    file.mime_type === 'text/plain' ||
                    file.mime_type === 'text/csv' ||
                    file.mime_type === 'application/json' ||
                    file.mime_type === 'application/msword' ||
                    file.mime_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                    file.mime_type === 'application/vnd.ms-excel' ||
                    file.mime_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                    file.mime_type === 'application/vnd.ms-powerpoint' ||
                    file.mime_type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') && (
                    <Button
                      data-testid={`preview-button-${index}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePreview(file)}
                      className="text-gray-600 hover:text-blue-600"
                      title="Preview"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    data-testid={`download-button-${index}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(file.id, file.filename)}
                    className="text-gray-600 hover:text-teal-600"
                    title="Download"
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-button-${index}`}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(file.id)}
                    className="text-gray-600 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <h3 className="font-medium text-gray-900 mb-2 truncate" title={file.filename}>
                {file.filename}
              </h3>
              <p className="text-sm text-gray-500 mb-1">{formatFileSize(file.file_size)}</p>
              <p className="text-xs text-gray-400">
                {new Date(file.uploaded_at).toLocaleDateString()}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Preview Modal */}
      <Dialog open={!!previewFile} onOpenChange={closePreview}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate pr-4">{previewFile?.filename}</span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(previewFile?.id, previewFile?.filename)}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(90vh-100px)]">
            {previewFile && (
              <>
                {previewFile.mime_type?.startsWith('image/') && previewUrl ? (
                  <img 
                    src={previewUrl}
                    alt={previewFile.filename}
                    className="w-full h-auto"
                  />
                ) : previewFile.mime_type === 'application/pdf' && previewUrl ? (
                  <div className="w-full h-[650px]">
                    <PDFViewer 
                      url={previewUrl}
                      filename={previewFile.filename}
                      onDownload={() => handleDownload(previewFile.id, previewFile.filename)}
                    />
                  </div>
                ) : previewFile.useGoogleViewer && previewUrl ? (
                  <div className="w-full h-[650px]">
                    <iframe 
                      src={previewUrl}
                      title={previewFile.filename}
                      className="w-full h-full border-0 rounded"
                      sandbox="allow-scripts allow-same-origin"
                    />
                  </div>
                ) : (previewFile.mime_type === 'text/plain' || 
                     previewFile.mime_type === 'text/csv' || 
                     previewFile.mime_type === 'application/json') && previewUrl ? (
                  <div className="w-full bg-gray-900 rounded-lg p-4 overflow-auto max-h-[600px]">
                    <iframe 
                      src={previewUrl}
                      title={previewFile.filename}
                      className="w-full h-[550px] bg-white rounded"
                    />
                  </div>
                ) : previewFile.isOfficeDoc ? (
                  <div className="text-center py-12">
                    <File className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Microsoft Office Document</h3>
                    <p className="text-gray-500 mb-6">
                      Office documents require download for viewing to maintain security.
                    </p>
                    <Button 
                      onClick={() => handleDownload(previewFile.id, previewFile.filename)}
                      className="bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download to View
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Preview not available for this file type</p>
                    <Button onClick={() => handleDownload(previewFile.id, previewFile.filename)}>
                      <Download className="w-4 h-4 mr-2" />
                      Download File
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Files;
